import {DepositAccountType, FundAccountType, IsaAccountType} from '$share/constants/bank'
import {AccountType as InvestAccountType} from '$share/constants/invest'

import {Account, PayAccount, PaymoneyAccount} from '../account'
import {AssetType, CommonDate} from '../common'

export type UniAccount =
    | UniDepositAccount
    | UniDepositOnlyPayAccount
    | UniFundAccount
    | UniIsaAccount
    | UniInvestAccount
    | UniInvestOnlyPayAccount

export type UniBankAccount = UniDepositAccount | UniDepositOnlyPayAccount | UniFundAccount | UniIsaAccount

export type UniDepositAccount = Account & {
    assetType: AssetType.DEPOSIT
    expDate: CommonDate | null
    payAcc: PayAccount | null
    nbb: false
    type: DepositAccountType // 계좌형식
    balanceAmt: number | null // (상세) 잔액
    balanceKrwAmt: number | null // (상세) 원화환산 잔액
    offeredRate: number | null // (상세) 적용금리
    evalAmt: null
    evalKrwAmt: null
    principal: null
    paymoney: PaymoneyAccount | null
}

export type UniDepositOnlyPayAccount = Omit<Account, 'id'> & {
    id: null
    assetType: AssetType.DEPOSIT
    expDate: CommonDate | null
    payAcc: PayAccount
    nbb: false
    type: DepositAccountType // 계좌형식
    balanceAmt: null
    balanceKrwAmt: null
    offeredRate: null
    evalAmt: null
    evalKrwAmt: null
    principal: null
    paymoney: PaymoneyAccount | null
}

export type UniFundAccount = Account & {
    expDate: CommonDate | null
    payAcc: null
    nbb: false
    type: FundAccountType // 계좌형식
    balanceAmt: number | null // (상세) 잔액
    balanceKrwAmt: number | null // (상세) 원화환산 잔액
    evalAmt: number | null // (상세) fund 계좌일 경우 평가금액
    evalKrwAmt: number | null // (상세) fund 계좌일 경우 원화환산 평가금액
    principalKrwAmt: number | null // (상세) fund 계좌일 경우 투자원금
    offeredRate: null
    assetType: AssetType.FUND
    paymoney: null
}

export type UniIsaAccount = Account & {
    expDate: CommonDate | null
    payAcc: null
    nbb: false
    type: IsaAccountType // 계좌형식
    balanceAmt: number | null // (상세) 잔액
    balanceKrwAmt: number | null // (상세) 원화환산 잔액
    evalAmt: number | null // (상세) isa 계좌일 경우 평가금액
    evalKrwAmt: number | null // (상세) isa 계좌일 경우 원화환산 평가금액
    principal: null
    offeredRate: null
    assetType: AssetType.ISA
    paymoney: null
}

export type UniInvestAccount = Account & {
    assetType: AssetType.INVEST
    expDate: CommonDate | null
    payAcc: PayAccount | null
    nbb: boolean // 네이버통장 여부
    type: InvestAccountType // 계좌유형
    balanceAmt: number | null // (상세) 출금가능금액
    balanceKrwAmt: number | null // (상세) 원화환산 출금가능금액
    withholdingsKrwAmt: number | null // (상세) 원화환산 예수금
    evalAmt: number | null // (상세) 상품 평가금액의 합
    evalKrwAmt: number | null // (상세) 상품 평가금액의 원화환산 합
    offeredRate: null
    principal: null
    paymoney: null
}

export type UniInvestOnlyPayAccount = Omit<Account, 'id'> & {
    id: null
    assetType: AssetType.INVEST
    expDate: CommonDate | null
    payAcc: PayAccount
    nbb: boolean // 네이버통장 여부
    type: InvestAccountType // 계좌유형
    balanceAmt: null
    balanceKrwAmt: null
    offeredRate: null
    evalAmt: null
    evalKrwAmt: null
    principal: null
    paymoney: null
}

export function isUniDepositAccount(account: UniAccount): account is UniDepositAccount {
    return account.assetType === AssetType.DEPOSIT && typeof account.id === 'number'
}
export function isUniDepositOnlyPayAccount(account: UniAccount): account is UniDepositOnlyPayAccount {
    return account.assetType === AssetType.DEPOSIT && account.id === null
}
export function isUniFundAccount(account: UniAccount): account is UniFundAccount {
    return account.assetType === AssetType.FUND
}
export function isUniIsaAccount(account: UniAccount): account is UniIsaAccount {
    return account.assetType === AssetType.ISA
}
export function isUniInvestAccount(account: UniAccount): account is UniInvestAccount {
    return account.assetType === AssetType.INVEST && typeof account.id === 'number'
}
export function isUniInvestOnlyPayAccount(account: UniAccount): account is UniInvestOnlyPayAccount {
    return account.assetType === AssetType.INVEST && account.id === null
}
